import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ChevronDownIcon } from 'lucide-react';
import { faqs } from '../data/faqs';
import { SUPPORT_EMAIL } from '../data/launch';
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const toggle = (index: number) => setOpenIndex(openIndex === index ? null : index);
  return <div className="min-h-screen bg-[#FAFAFA] font-sans selection:bg-kitty-yellow-200 selection:text-kitty-dark" data-id="element-502" data-mp="3pvn8hm07fng1">
      <SEO title="FAQ | KittyPot" description="Answers about KittyPot: kitties, invites, splitting expenses, settling up, currencies, receipts, deleting your account and how your data is protected." ogTitle="KittyPot FAQ" ogDescription="Common questions about tracking shared expenses with KittyPot." path="/faq" jsonLd={{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }} data-id="element-503" data-mp="3kxvduk86dn85" />
      <Navbar data-id="element-504" data-mp="29curh51mm779" />
      <main className="pt-36 pb-24" data-id="element-505" data-mp="29ba5i3tdnwl0">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-506" data-mp="2ddwrho3danaa">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-center mb-14" data-id="element-507" data-mp="2dcbza54izgfl">
            <h1 className="text-4xl md:text-5xl font-black text-kitty-dark mb-4 tracking-tight" data-id="element-508" data-mp="2s3kvix2xo01p">
              Frequently asked questions
            </h1>
            <p className="text-lg text-gray-500 max-w-lg mx-auto" data-id="element-509" data-mp="2x374uvfn4w58">
              Everything you need to know about KittyPot. Can't find what you're
              looking for?{' '}
              <Link to="/contact" className="text-kitty-yellow-700 hover:text-kitty-yellow-800 font-semibold underline underline-offset-2" data-id="element-510" data-mp="22gi1tt39fvwz">
                Get in touch
              </Link>
              .
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="space-y-3" data-id="element-511" data-mp="2q48cpy98qvjl">
            {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return <div key={faq.question} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-512" data-mp="2v4nnci251oxv" data-mpi={index}>
                  <h2 data-id="element-513" data-mp="10r4x7taft8ml">
                    <button onClick={() => toggle(index)} aria-expanded={isOpen} aria-controls={`faq-panel-${index}`} id={`faq-button-${index}`} className="w-full flex items-center justify-between px-6 py-5 text-left gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow focus-visible:ring-inset" data-id="element-514" data-mp="10ro5sje76kny">
                      <span className="text-base font-semibold text-kitty-dark leading-snug" data-id="element-515" data-mp="2tmfmh9jx4g65">
                        {faq.question}
                      </span>
                      <motion.span animate={{
                    rotate: isOpen ? 180 : 0
                  }} transition={{
                    duration: 0.2,
                    ease: [0.22, 1, 0.36, 1]
                  }} className="flex-shrink-0" data-id="element-516" data-mp="2xmfzcprdcjb3">
                        <ChevronDownIcon className="w-5 h-5 text-gray-500" aria-hidden="true" data-id="element-517" data-mp="hyfyo196prap" />
                      </motion.span>
                    </button>
                  </h2>
                  <AnimatePresence initial={false} data-id="element-518" data-mp="2ix9ovi0hpkfz">
                    {isOpen && <motion.div id={`faq-panel-${index}`} role="region" aria-labelledby={`faq-button-${index}`} initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25,
                  ease: [0.22, 1, 0.36, 1]
                }} className="overflow-hidden" data-id="element-519" data-mp="2dygfo1s8kflm">
                        <div className="px-6 pb-5 pt-0" data-id="element-520" data-mp="1m9n0869nr6d3">
                          <p className="text-gray-600 leading-relaxed text-[15px]" data-id="element-521" data-mp="1mb7sfp8i2d7s">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </motion.div>

          {/* Bottom CTA */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="text-center mt-16 bg-gray-950 rounded-2xl p-10" data-id="element-522" data-mp="3swc8htd1qf3p">
            <h2 className="text-2xl font-bold text-white mb-3" data-id="element-523" data-mp="5qtkz2nxy5eg">
              Still have questions?
            </h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto" data-id="element-524" data-mp="3a75mnxgyh7do">
              We're happy to help. Reach out and we'll get back to you as soon
              as we can.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-525" data-mp="3e6wjoix2sjqn">
              <Link to="/contact" className="bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-3 rounded-full font-bold transition-colors duration-150 active:scale-95 shadow-md hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-526" data-mp="3j1ka4nbn3o7v">
                Contact us
              </Link>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-400 hover:text-white font-medium transition-colors text-sm underline underline-offset-2" data-id="element-527" data-mp="3ou6hdn01cv99">
                or email {SUPPORT_EMAIL}
              </a>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-528" data-mp="3axav2surx7b9" />
    </div>;
}